import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BasicTable,
  Breadcrumbs,
  CardComponent,
  DashedStandardDropdown,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import DateFilter from '../../_components/molecules/DateFilter';
import {
  CategoryDetails,
  CategoryList,
  formatCategoryDetails,
  getBenchmarkCategoryColumn,
  getCategoryName,
} from '../../core-utils/helper-utils/benchmarkHelper';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { benchmarkConstants } from '../../core-utils/messages/benchmarkConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStores } from '../../stores';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import Snackbar from '../../_components/atoms/Snackbar';
import {
  getCategoryDetails,
  getCategoryDetailsSummary,
} from '../../core-utils/axios/benchmark/benchmark';
import Loader from '../../_components/molecules/Loader';
import { CMS_BENCHMARK } from '../../core-utils/routes';
import { formatMemberClaim } from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import properties from '../../core-utils/properties';
import axios from 'axios';
import {
  handleHeaderMouseEnter,
  handleHeaderMouseLeave,
} from '../../core-utils/helper-utils/tableSortHelper';
import { Pagination } from '@mui/material';
import {
  checkUserAccess,
  UserAccessConstants,
} from '../../core-utils/helper-utils/userAccess';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModalNew';
import { useCohortModal } from '../../core-utils/custom-hooks/useCohortModal';
import CohortFilterBar from '../../_components/molecules/CohortFilterBar';
import { useCohortOnPageLoad } from '../../core-utils/custom-hooks/useCohortPageOnLoad';
import { observer } from 'mobx-react';

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '1230px',
    padding: '40px 0px 66px 63px',
  },
  hr: {
    width: '100%',
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    margin: '18px 0px',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  flex: {
    display: 'flex',
  },
  tableGrid: {
    overflowX: 'auto',
    width: '1130px',
    ...scrollBarStyles,
  },
});

const BenchmarkInternalPage = observer(() => {
  const { classes } = useStyles();

  const messages = benchmarkConstants;
  const { state } = useLocation() as {
    state: {
      categoryKey: string;
      categoryList: CategoryList[];
      categoryDescription: string;
    };
  };
  const {
    employerGroupStore,
    quaterlyReportStore,
    loginPageStore,
    cohortDetailsStore,
  } = useStores();
  const egNid = employerGroupStore?.employerGroupResponseData?.eg_nid[0];
  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
  } = quaterlyReportStore;
  const navigate = useNavigate();

  const { getPlanDates, loading: isPlanDatesLoading } = usePlanDates();
  const payload = useAPIPayload();
  const { snackbarState, handleCloseSnackBar, openSnackbar } = useSnackbar();
  const { open, openModal, closeModal, isEditMode } = useCohortModal();

  const pageSize = 10;

  const [loading, setLoading] = useState<boolean>(false);
  const [categoryDropdownValue, setCategoryDropdownValue] = useState<string>(
    state.categoryKey,
  );
  const [categoryDetails, setCategoryDetails] = useState<CategoryDetails[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('total_spend');
  const [sortOrder, setSortOrder] = useState<number>(-1);
  const [columnId, setColumnId] = useState<string>('');

  const { totalResults, memberIds, isInitializing } = useCohortOnPageLoad({
    fetchTotalResults: () =>
      getCategoryDetailsSummary(payload, categoryDropdownValue),
    enabled:
      isPlanDatesFetched &&
      !!employerGroupStore?.employerGroupResponseData?.eg_nid?.[0],
    isInternal: true,
    deps: [categoryDropdownValue],
  });

  const breadcrumbItemList = [
    [
      'text',
      'CMS Benchmarking',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => navigate(CMS_BENCHMARK),
    ],
    [
      'text',
      getCategoryName(state.categoryList, categoryDropdownValue),
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const handlePagination = (event: ChangeEvent<unknown>, page: number) => {
    setPageNumber(page);
  };
  const handleSort = (event: any, column: any, item: any) => {
    const sortOrder = item.actionType === 'ascendingSorting' ? 1 : -1;
    const { sortColumnName } = column;
    if (!sortColumnName) {
      return;
    }

    setSortField(sortColumnName);
    setSortOrder(sortOrder);
  };

  const apiCalls = async () => {
    setLoading(true);
    try {
      if (isPlanDatesFetched) {
        const responses = await Promise.allSettled([
          getCategoryDetails(
            payload,
            categoryDropdownValue,
            pageNumber - 1,
            cohortDetailsStore.memberUniqueIdList,
            pageSize,
            sortField,
            sortOrder,
          ),
        ]);

        if (responses[0].status === 'fulfilled') {
          const updatedData = responses[0].value.data?.data?.categoryData;

          const promises = updatedData.map((item: CategoryDetails) => {
            return axios.post(
              properties.aiqBaseUrl +
                properties.membersBaseUrl +
                properties.memberDetails,
              {
                eg_nid: [egNid],
                claim_id_list: item.claimIds,
              },
            );
          });

          const memberDetailsResponses = await Promise.all(promises);

          const finalRows = updatedData.map((row: any, index: number) => {
            const apiResponse = memberDetailsResponses[index];
            const medicalData = apiResponse?.data?.data ?? [];

            const formatted = formatMemberClaim(medicalData);

            return {
              ...row,
              riskPatientDetails: formatted,
            };
          });
          setCategoryDetails(finalRows);
          setPageCount(
            Math.ceil(responses[0].value.data?.data.totalCount / pageSize),
          );
        }
      }
    } catch (error) {
      openSnackbar(messages.ERROR, 'error', 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };

    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  useEffect(() => {
    if (isInitializing) {
      return;
    }

    apiCalls();
  }, [
    isInitializing,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
    pageNumber,
    sortField,
    sortOrder,
    cohortDetailsStore.conditionsDetails,
  ]);

  const isPageLoading = isPlanDatesLoading || loading || isInitializing;

  return isPageLoading ? (
    <Loader />
  ) : (
    <>
      <Grid className={classes.root}>
        <Grid style={{ marginBottom: '1rem' }}>
          <Breadcrumbs itemList={breadcrumbItemList} />
        </Grid>

        <PageTitleAndSubTitle
          title={
            <DashedStandardDropdown
              options={state.categoryList.map((cat) => cat.category_key)}
              displayValue={state.categoryList.map((cat) => cat.category_name)}
              value={categoryDropdownValue}
              handleChange={(event: any) => {
                const value: string = event.target.value as string;
                setCategoryDropdownValue(value);
                setPageNumber(1);
              }}
            />
          }
          subTitle={state.categoryDescription}
          subTitleStyles={{ width: '100%' }}
          chip={
            <Grid className={classes.flex}>
              <Typography
                fontSize="17px"
                lineHeight="21px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                fontType="semiBold"
                margin="2px 8px 0px 0px"
              >
                for
              </Typography>
              <DateFilter />
            </Grid>
          }
          showQueryDataButton={checkUserAccess(
            UserAccessConstants.COHORT_DETAILS,
            loginPageStore.userResponse.role.name,
            loginPageStore.userFeatures,
          )}
          queryButtonText={
            cohortDetailsStore.cohortDetailsList?.length > 0 &&
            cohortDetailsStore.cohortDetailsList[0].category !== ''
              ? 'Edit Cohort'
              : 'Add Cohort'
          }
          queryDataAction={() => {
            if (
              checkUserAccess(
                UserAccessConstants.COHORT_DETAILS,
                loginPageStore.userResponse.role.name,
                loginPageStore.userFeatures,
              )
            ) {
              openModal();
            }
          }}
        />
        <hr className={classes.hr} />
        <Grid className={classes.mainContainer}>
          <CohortFilterBar />
          <CardComponent
            title={messages.TABLE_INTERNAL}
            titleSubTitleMargin="1rem"
            body={
              <Grid className={classes.tableGrid}>
                <Grid
                  style={{
                    width:
                      getBenchmarkCategoryColumn().length > 4
                        ? `${getBenchmarkCategoryColumn().length * 200}px`
                        : '1130px',
                    paddingBottom: '0.5rem',
                  }}
                >
                  <BasicTable
                    columns={getBenchmarkCategoryColumn()}
                    rowsData={formatCategoryDetails(categoryDetails)}
                    displayType="row"
                    isFullTable
                    headerCellHeight="46px"
                    headerColor={patientAccessTheme.palette.grey[700]}
                    headerTextColor={patientAccessTheme.palette.status.white}
                    cellHeight="46px"
                    cellPadding="12px 9px 12px 14px"
                    pageCount={pageCount}
                    pageNumber={pageNumber}
                    isCollapsiblePatientTable={true}
                    handleMenuItem={handleSort}
                    showOnHoverHeader={true}
                    columnId={columnId}
                    handleHeaderMouseEnter={(column: any) =>
                      handleHeaderMouseEnter(column, setColumnId)
                    }
                    handleHeaderMouseLeave={() =>
                      handleHeaderMouseLeave(setColumnId)
                    }
                  />
                </Grid>
                <Pagination
                  page={pageNumber}
                  count={pageCount}
                  onChange={handlePagination}
                  size="small"
                  color="primary"
                  style={{
                    margin: '20px 0px',
                  }}
                />
              </Grid>
            }
          />
        </Grid>
      </Grid>
      <CohortDetailsModal
        open={open}
        handleClose={closeModal}
        totalResults={totalResults}
        isEditMode={isEditMode}
        existingMemberUniqueIds={memberIds}
      />

      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default BenchmarkInternalPage;
